import useResizeObserver from "@react-hook/resize-observer";
import { RefObject, useEffect, useState } from "react";

/**
 * Custom hook for detecting if a container's width is below a given pixel breakpoint.
 * Uses ResizeObserver to respond to container size changes, which is useful when
 * the container can be resized independently of the viewport (e.g., sidebar panels).
 *
 * @param containerRef - React ref to the container element to observe, or the
 *   element itself. Pass the element (held in state via a callback ref) when the
 *   container can unmount and remount: a ref object keeps the same identity, so
 *   the observer would stay bound to the detached node and freeze the result.
 * @param breakpoint - Width in pixels below which to return true
 * @returns boolean indicating if the container width is below the breakpoint
 *
 * WARNING: Do NOT call this hook multiple times with the same containerRef from different
 * components. The underlying @react-hook/resize-observer uses a singleton ResizeObserver
 * with a shared callbacks Map. When multiple components subscribe to the same element,
 * the unsubscribe logic can corrupt the callbacks array (if cbs.length === 1, it deletes
 * ALL callbacks for that target), causing some components to miss resize updates.
 * Instead, call this hook once in a common parent and pass the boolean down as props.
 *
 * @example
 * const containerRef = useRef<HTMLDivElement>(null);
 * const isCompact = useContainerBreakpoint(containerRef, 420);
 *
 * return (
 *   <div ref={containerRef}>
 *     <Button collapseToIcon={isCompact}>Action</Button>
 *   </div>
 * );
 */
export const useContainerBreakpoint = (
  containerRef: RefObject<HTMLElement | null> | HTMLElement | null,
  breakpoint: number,
): boolean => {
  const [isBelowBreakpoint, setIsBelowBreakpoint] = useState(false);

  // Set initial width on mount (and on remount, when given an element)
  useEffect(() => {
    const element =
      containerRef instanceof HTMLElement
        ? containerRef
        : containerRef?.current;
    if (element) {
      setIsBelowBreakpoint(element.offsetWidth < breakpoint);
    }
  }, [containerRef, breakpoint]);

  // Track container width changes - pass null to disable observer when no ref
  useResizeObserver(containerRef, (entry) => {
    setIsBelowBreakpoint(entry.contentRect.width < breakpoint);
  });

  // If no ref provided, never trigger breakpoint (non-responsive mode)
  if (!containerRef) return false;

  return isBelowBreakpoint;
};
